import { averageMeetsMinimum } from "./averageCatalogs";
import { searchPeople } from "./bowlerMatch";
import {
  lastFirstKey,
  nameKey,
  normalizeUsbcId,
  uniqueIndex,
} from "./bowlerMatchKeys";

export {
  lastFirstKey,
  nameKey,
  normalizeMatchName,
  normalizeUsbcId,
} from "./bowlerMatchKeys";

export const highestQualifyingAverage = (averages, minGamesForAverage) => {
  const rows = (averages || []).filter(
    (row) =>
      row &&
      row.average != null &&
      Number.isFinite(Number(row.average)) &&
      averageMeetsMinimum(row.games, minGamesForAverage)
  );
  if (rows.length === 0) return null;
  return rows.reduce((best, row) =>
    Number(row.average) > Number(best.average) ? row : best
  );
};

export const matchCatalogToTournamentBowlers = (
  tournamentBowlers,
  catalogPeople
) => {
  const byUsbc = uniqueIndex(tournamentBowlers, (bowler) =>
    normalizeUsbcId(bowler.usbcID)
  );
  const byFullName = uniqueIndex(tournamentBowlers, (bowler) =>
    nameKey(bowler.name)
  );
  const byLastFirst = uniqueIndex(tournamentBowlers, (bowler) =>
    lastFirstKey(bowler.name)
  );

  const claimedCatalog = new Set();
  const autoMatchedBowlers = new Set();
  const matches = [];

  const claim = (bowler, catalogPerson, status, reason) => {
    if (!bowler || claimedCatalog.has(catalogPerson.id)) return false;
    claimedCatalog.add(catalogPerson.id);
    if (status === "autoMatched") autoMatchedBowlers.add(bowler.id);
    matches.push({
      bowlerID: bowler.id,
      extractedBowlerID: catalogPerson.id,
      status,
      reason,
      catalogPerson,
    });
    return true;
  };

  for (const person of catalogPeople) {
    const usbc = normalizeUsbcId(person.usbcId);
    if (usbc && byUsbc.unique.has(usbc)) {
      claim(byUsbc.unique.get(usbc), person, "autoMatched", "usbc");
    }
  }

  for (const person of catalogPeople) {
    if (matches.some((row) => row.extractedBowlerID === person.id)) continue;
    const full = nameKey(person.name);
    if (full && byFullName.unique.has(full)) {
      claim(byFullName.unique.get(full), person, "autoMatched", "exactName");
      continue;
    }
    const lastFirst = lastFirstKey(person.name);
    if (lastFirst && byLastFirst.unique.has(lastFirst)) {
      claim(
        byLastFirst.unique.get(lastFirst),
        person,
        "autoMatched",
        "lastFirst"
      );
      continue;
    }
    const fullCandidates = (full && byFullName.buckets.get(full)) || [];
    const lastFirstCandidates =
      (lastFirst && byLastFirst.buckets.get(lastFirst)) || [];
    const candidates = [...new Set([...fullCandidates, ...lastFirstCandidates])];
    const open = candidates.filter((bowler) => !autoMatchedBowlers.has(bowler.id));
    if (open.length > 1) {
      matches.push({
        bowlerID: null,
        extractedBowlerID: person.id,
        status: "possibleMatch",
        reason: "multipleNames",
        catalogPerson: person,
        candidates: open,
      });
    }
  }

  return collapseAutoMatches(matches);
};

const suggestionForPerson = (person, minGamesForAverage, status, reason) => {
  const candidate = highestQualifyingAverage(
    person.averages,
    minGamesForAverage
  );
  return {
    extractedBowlerID: person.id,
    name: person.name,
    usbcId: person.usbcId,
    status,
    reason,
    average: candidate?.average ?? null,
    games: candidate?.games ?? null,
    source:
      candidate?.source || person.leagueName || person.fileName || "Catalog",
  };
};

export const searchCatalogPeopleForTeamCreate = ({
  query,
  usbcID,
  email,
  phone,
  writtenAverage,
  catalogPeople,
  minGamesForAverage,
  excludeIds = [],
  limit = 8,
}) =>
  searchPeople({
    query,
    usbcID,
    email,
    phone,
    writtenAverage,
    people: catalogPeople,
    usbcOf: (person) => person.usbcId || person.usbcID,
    emailOf: (person) => person.email,
    phoneOf: (person) => person.phone,
    averageOf: (person) =>
      highestQualifyingAverage(person.averages, minGamesForAverage)?.average ??
      null,
    excludeIds,
    limit,
  }).map((row) =>
    suggestionForPerson(row.person, minGamesForAverage, row.status, row.reason)
  );

export const mergeCatalogPeople = (people = []) => {
  const list = (people || []).filter(Boolean);
  if (list.length === 0) return null;
  const withSheetMeta = list.map((person) => ({
    ...person,
    averages: (person.averages || []).map((average, index) => ({
      ...average,
      id: average.id || `${person.id}-${index}`,
      source:
        average.source ||
        person.leagueName ||
        person.fileName ||
        "Catalog",
      storagePath: average.storagePath || person.storagePath || null,
      mimeType: average.mimeType || person.mimeType || null,
      fileName: average.fileName || person.fileName || null,
      leagueName: average.leagueName || person.leagueName || null,
      sourcePage: average.sourcePage ?? person.sourcePage ?? null,
      boundingBox: average.boundingBox ?? person.boundingBox ?? null,
    })),
  }));
  if (withSheetMeta.length === 1) return withSheetMeta[0];
  const primary = withSheetMeta[0];
  return {
    ...primary,
    averages: withSheetMeta.flatMap((person) => person.averages),
  };
};

const collapseAutoMatches = (matches) => {
  const grouped = new Map();
  const others = [];
  for (const match of matches) {
    if (match.status === "autoMatched" && match.bowlerID) {
      const rows = grouped.get(match.bowlerID) || [];
      rows.push(match);
      grouped.set(match.bowlerID, rows);
    } else {
      others.push(match);
    }
  }
  const merged = [...grouped.values()].map((rows) => ({
    ...rows[0],
    catalogPerson: mergeCatalogPeople(rows.map((row) => row.catalogPerson)),
  }));
  return [...merged, ...others];
};

export const catalogIdentityKey = (row) => {
  const usbc = normalizeUsbcId(row?.usbcId || row?.usbcID);
  if (usbc) return `usbc:${usbc}`;
  const name = nameKey(row?.name);
  return name ? `name:${name}` : "";
};

const betterCatalogBook = (left, right) => {
  const leftAvg = left?.average == null ? Number.NEGATIVE_INFINITY : Number(left.average);
  const rightAvg = right?.average == null ? Number.NEGATIVE_INFINITY : Number(right.average);
  if (rightAvg !== leftAvg) return rightAvg > leftAvg ? right : left;
  const leftGames = Number(left?.games) || 0;
  const rightGames = Number(right?.games) || 0;
  return rightGames > leftGames ? right : left;
};

export const uniqueAutoCatalogMatch = (suggestions, { name, usbcID } = {}) => {
  const list = suggestions || [];
  const wantedUsbc = normalizeUsbcId(usbcID);
  const wantedName = nameKey(name);
  const scoped = list.filter((row) => {
    if (wantedUsbc) {
      const rowUsbc = normalizeUsbcId(row.usbcId || row.usbcID);
      if (rowUsbc) return rowUsbc === wantedUsbc;
    }
    if (wantedName) return nameKey(row.name) === wantedName;
    return true;
  });
  const pool = scoped.length ? scoped : list;

  const groups = new Map();
  pool.forEach((row) => {
    if (row?.status === "searchHit") return;
    const key = catalogIdentityKey(row);
    if (!key) return;
    const current = groups.get(key) || [];
    current.push(row);
    groups.set(key, current);
  });
  if (groups.size === 1) {
    const rows = [...groups.values()][0];
    const alreadyAuto = rows.some((row) => row.status === "autoMatched");
    if (rows.length > 1 || alreadyAuto) {
      const best = rows.reduce(betterCatalogBook);
      return {
        ...best,
        status: "autoMatched",
        reason: best.reason || "exactName",
      };
    }
  }

  const auto = pool.filter((row) => row.status === "autoMatched");
  return auto.length === 1 ? auto[0] : null;
};

export const enteredAverageValue = (bowler) => {
  if (bowler?.hasAverage === false) return null;
  if (bowler?.average == null || bowler.average === "") return null;
  const value = Number(bowler.average);
  return Number.isFinite(value) ? value : null;
};

export const catalogAgreesWithEntered = (suggestion, bowler) => {
  if (!suggestion) return false;
  const entered = enteredAverageValue(bowler);
  if (suggestion.average == null) return entered == null;
  if (entered == null) return false;
  return Number(suggestion.average) === entered;
};

const orgBowlerAverage = (bowler) => {
  if (!bowler || bowler.isNew || bowler.hasAverage === false) return null;
  if (bowler.average == null || bowler.average === "") return null;
  const value = Number(bowler.average);
  return Number.isFinite(value) ? value : null;
};

export const resolveCatalogAverageForBowler = ({
  currentBowler,
  catalogSuggestions = [],
  selectedOrgBowler = null,
} = {}) => {
  const unique = uniqueAutoCatalogMatch(catalogSuggestions, {
    name: selectedOrgBowler?.name || currentBowler?.name,
    usbcID:
      selectedOrgBowler?.usbcID ||
      selectedOrgBowler?.usbcId ||
      currentBowler?.usbcID ||
      currentBowler?.usbcId,
  });
  const entered = enteredAverageValue(currentBowler);
  const catalogAverage =
    unique?.average == null || unique?.average === ""
      ? null
      : Number(unique.average);
  const catalogMatch = unique || null;
  const averageDisagrees = Boolean(
    entered != null && catalogAverage != null && Number(catalogAverage) !== entered
  );

  if (entered != null) {
    return {
      average: entered,
      hasAverage: true,
      averagePopulated: Boolean(currentBowler?.averagePopulated) && !averageDisagrees,
      catalogMatch,
      averageDisagrees,
      catalogSuggestions: catalogMatch ? [] : catalogSuggestions,
    };
  }

  if (catalogAverage != null) {
    return {
      average: Math.round(catalogAverage),
      hasAverage: true,
      averagePopulated: true,
      catalogMatch,
      averageDisagrees: false,
      catalogSuggestions: [],
    };
  }

  if (unique && unique.average == null) {
    return {
      average: "",
      hasAverage: false,
      averagePopulated: false,
      catalogMatch,
      averageDisagrees: false,
      catalogSuggestions: [],
    };
  }

  const fromOrg = orgBowlerAverage(selectedOrgBowler);
  if (fromOrg != null) {
    return {
      average: fromOrg,
      hasAverage: true,
      averagePopulated: true,
      catalogMatch: null,
      averageDisagrees: false,
      catalogSuggestions,
    };
  }

  return {
    average: currentBowler?.hasAverage === false ? "" : currentBowler?.average ?? "",
    hasAverage: currentBowler?.hasAverage !== false,
    averagePopulated: Boolean(currentBowler?.averagePopulated),
    catalogMatch: null,
    averageDisagrees: false,
    catalogSuggestions,
  };
};
